import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { t } from '@aquabox/shared';
import { api, ApiError } from '../api/client';

const MIN_LENGTH = 10;

/** Doğrulama sonucu: hata metni ya da null. Sunucu da aynı kuralı uygular (≥10). */
export function validateNewPassword(password: string, confirm: string): string | null {
  if (password.length < MIN_LENGTH) return t('auth.passwordTooShort');
  if (password !== confirm) return t('panel.passwordReset.mismatch');
  return null;
}

/**
 * Parola sıfırlama (`/sifre-sifirla#token=…`). Jeton query'de değil FRAGMENT'te: tarayıcı
 * fragment'i sunucuya göndermez (proxy/Cloudflare günlüğüne düşmez). Mount'ta okunup state'e
 * alınır ve adres çubuğundan HEMEN silinir; hata/terk durumunda da adreste kalmaz. Ekrana ve
 * console'a basılmaz.
 */
export function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const [token] = useState<string | null>(() =>
    new URLSearchParams(location.hash.replace(/^#/, '')).get('token'),
  );
  useEffect(() => {
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + window.location.search,
    );
  }, []);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!token) return;
    const invalid = validateNewPassword(password, confirm);
    if (invalid) {
      setError(invalid);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/auth/password-reset', { token, password });
      navigate('/', { replace: true, state: { notice: t('panel.passwordReset.success') } });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('error.internal'));
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--page-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <form
        onSubmit={(e) => void submit(e)}
        className="card"
        style={{
          width: '100%',
          maxWidth: 420,
          padding: 36,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 24,
            fontWeight: 600,
            textAlign: 'center',
          }}
        >
          {t('panel.passwordReset.title')}
        </h2>

        {!token ? (
          <div role="alert" style={{ color: 'var(--danger)', fontSize: 13.5, fontWeight: 500 }}>
            {t('panel.passwordReset.noToken')}
          </div>
        ) : (
          <>
            <div>
              <label className="label" htmlFor="new-password">
                {t('panel.passwordReset.password')}
              </label>
              <input
                id="new-password"
                className="input"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 4 }}>
                {t('panel.passwordReset.hint')}
              </div>
            </div>
            <div>
              <label className="label" htmlFor="confirm-password">
                {t('panel.passwordReset.confirm')}
              </label>
              <input
                id="confirm-password"
                className="input"
                type="password"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
            {error && (
              <div
                role="alert"
                style={{
                  background: 'var(--bg-danger-soft)',
                  border: '1px solid var(--danger)',
                  color: 'var(--danger)',
                  borderRadius: 'var(--r-input)',
                  padding: '10px 14px',
                  fontSize: 13.5,
                  fontWeight: 500,
                }}
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              className={`btn btn-primary ${busy ? 'btn-loading' : ''}`}
              disabled={busy}
              style={{ height: 52, justifyContent: 'center' }}
            >
              {busy && <span className="spinner" />}
              {busy ? t('panel.passwordReset.submitting') : t('panel.passwordReset.submit')}
            </button>
          </>
        )}

        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate('/', { replace: true })}
        >
          {t('panel.passwordReset.backToLogin')}
        </button>
      </form>
    </div>
  );
}
